import {
  CheckCircleOutline,
  Dns,
  ErrorOutline,
  Link,
  Replay,
} from "@mui/icons-material";
import {
  Alert,
  AlertTitle,
  alpha,
  Box,
  Button,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import CardConnectivity from "./cardConnectivity";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../../../../services/api";

export default function ConnectivityTests(props) {
  const { form, setTestsLocal, onTestingChange, proxys } = props;
  const theme = useTheme();
  const abortControllerRef = useRef(null);
  const initialTests = {
    PING: { loading: false, data: { title: "Ping", type: "PING" } },
    SNMP: {
      loading: false,
      data: { title: "SNMP", type: "SNMP" },
    },
    ACCESS: {
      loading: false,
      data: { title: `Acesso (${form?.tipoAcesso})`, type: "ACCESS" },
    },
  };
  const hasSnmp = ["SNMPv1", "SNMPv2"].includes(form?.tipoSNMP);
  const hasAccess =
    ["SSH", "Telnet"].includes(form?.tipoAcesso) && !!form?.user;

  const [tests, setTests] = useState(initialTests);
  const machineId = useMemo(
    () => proxys?.find((p) => p.proxyid === form?.proxy)?.description || null,
    [proxys, form?.proxy],
  );

  useEffect(() => {
    const currentLoading = Object.values(tests).some((t) => t.loading);
    onTestingChange(currentLoading);
  }, [tests, onTestingChange]);

  const updateTest = (type, partial) => {
    setTests((prev) => ({
      ...prev,
      [type]: { ...prev[type], ...partial },
    }));
  };

  async function runPing(signal) {
    updateTest("PING", { loading: true });
    try {
      const res = await api.post(
        "/easyAdd/zabbix/testHostPing",
        {
          host: form?.ip,
          machineId: machineId || "",
        },
        { signal },
      );

      updateTest("PING", {
        loading: false,
        data: {
          ...tests.PING.data,
          status: res?.data?.status,
          message: res?.data?.message,
          timerExec: res?.data?.avgPing,
        },
      });
      setTestsLocal((prev) => ({
        ...prev,
        ping: res?.data?.status,
      }));
    } catch (e) {
      if (e.name === "CanceledError" || e.name === "AbortError") return;
      updateTest("PING", {
        loading: false,
        data: { ...tests.PING.data, status: false, message: "Erro no Ping" },
      });
    }
  }

  async function runSnmp(signal) {
    updateTest("SNMP", { loading: true });
    try {
      const res = await api.post(
        "/easyAdd/zabbix/testHostSnmp",
        {
          machineId: machineId || "",
          host: form?.ip,
          snmpPort: form?.portaSNMP,
          snmpCommunity: form?.comunidadeSnmp,
        },
        { signal },
      );
      updateTest("SNMP", {
        loading: false,
        data: {
          ...tests.SNMP.data,
          status: res?.data?.status,
          message: res?.data?.message,
          timerExec: res?.data?.timeExec,
        },
      });
      setTestsLocal((prev) => ({
        ...prev,
        snmp: res?.data?.status,
      }));
    } catch (e) {
      if (e.name === "CanceledError" || e.name === "AbortError") return;
      updateTest("SNMP", {
        loading: false,
        data: { ...tests.SNMP.data, status: false, message: "Erro SNMP" },
      });
    }
  }

  async function runAccess(signal) {
    updateTest("ACCESS", { loading: true });
    try {
      let res;
      if (form?.tipoAcesso === "SSH") {
        res = await api.post(
          "/easyAdd/zabbix/testHostSsh",
          {
            machineId: machineId || "",
            host: form?.ip,
            sshUser: form?.user,
            sshPass: form?.senha,
            sshPort: form?.porta,
          },
          { signal },
        );
      } else {
        res = await api.post(
          "/easyAdd/zabbix/testHostTelnet",
          {
            machineId: machineId || "",
            host: form?.ip,
            telnetUser: form?.user,
            telnetPass: form?.senha,
            telnetPort: form?.porta,
          },
          { signal },
        );
      }

      updateTest("ACCESS", {
        loading: false,
        data: {
          ...tests.ACCESS.data,
          status: res?.data?.status,
          message: res?.data?.message,
          timerExec: res?.data?.timeExec,
        },
      });
    } catch (e) {
      if (e.name === "CanceledError" || e.name === "AbortError") return;
      updateTest("ACCESS", {
        loading: false,
        data: {
          ...tests.ACCESS.data,
          status: false,
          message: "Erro de Acesso",
        },
      });
    }
  }

  const runAll = useCallback(async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const newController = new AbortController();
    abortControllerRef.current = newController;

    setTests(initialTests);
    await runPing(newController.signal);
    if (hasSnmp && !newController.signal.aborted)
      await runSnmp(newController.signal);
    if (hasAccess && !newController.signal.aborted)
      await runAccess(newController.signal);
  }, []);

  useEffect(() => {
    if (!form) return;
    runAll();
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [form, runAll]);

  const cards = [
    {
      key: "PING",
      enabled: true,
      testState: tests.PING,
    },
    {
      key: "SNMP",
      enabled: hasSnmp,
      testState: tests.SNMP,
    },
    {
      key: "ACCESS",
      enabled: hasAccess,
      testState: tests.ACCESS,
    },
  ];
  const results = Object.values(tests).filter(
    (t) => t.data.status !== undefined,
  );
  const successCount = results.filter((t) => t.data.status === true).length;
  const errorCount = results.filter((t) => t.data.status === false).length;

  return (
    <Stack
      sx={{
        display: "flex",
        width: "100%",
        flexDirection: "column",
        gap: 2,
        pt: 2,
      }}
    >
      <Stack
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: 1,
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          width: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            backgroundColor: theme.palette.background.card,
            borderRadius: 1,
            padding: 1.5,
            width: "100%",
          }}
        >
          <Box sx={{ width: "75%" }}>
            <Stack
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "flex-start", sm: "center" },
                justifyContent: "flex-start",
                gap: 1,
              }}
            >
              <Stack
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 1,
                  alignItems: "center",
                }}
              >
                <Dns fontSize="inherit" />
                <Typography color="text.secondary" fontSize={"14px"}>
                  Host:
                </Typography>
                <Typography fontSize={"13px"}>{form?.ip}</Typography>
              </Stack>
              <Typography display={{ xs: "none", sm: "flex" }}>|</Typography>
              <Stack
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 1,
                  alignItems: "center",
                }}
              >
                <Link fontSize="inherit" />
                <Typography color="text.secondary" fontSize={"14px"}>
                  Interface:
                </Typography>
                <Typography fontSize={"13px"}>{form?.tipoSNMP}</Typography>
              </Stack>
            </Stack>
            {cards.every((card) => card?.testState?.loading === false) && (
              <Stack
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  gap: 1,
                }}
              >
                <Stack
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: 0.5,
                    alignItems: "center",
                    color: theme.palette.sucess.main,
                  }}
                >
                  <CheckCircleOutline fontSize="inherit" />
                  <Typography fontSize={"14px"}>{successCount}</Typography>
                </Stack>
                <Stack
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: 0.5,
                    alignItems: "center",
                    color: theme.palette.error.main,
                  }}
                >
                  <ErrorOutline fontSize="inherit" />
                  <Typography fontSize={"14px"}>{errorCount}</Typography>
                </Stack>
              </Stack>
            )}
          </Box>
          <Box
            sx={{
              width: "25%",
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
            }}
          >
            <Button
              variant="outlined"
              size="small"
              sx={{
                height: 48,
                backgroundColor: theme.palette.background.card,
                borderColor: theme.palette.text.primary,
                color: theme.palette.text.primary,
                ":hover": {
                  backgroundColor: alpha(theme.palette.text.primary, 0.08),
                  borderColor: theme.palette.text.primary,
                },
              }}
              onClick={runAll}
              startIcon={<Replay />}
            >
              Testar novamente
            </Button>
          </Box>
        </Box>
      </Stack>
      {cards
        .filter((card) => card.enabled)
        .map((card) => (
          <CardConnectivity
            key={card.key}
            loading={card.testState.loading}
            testData={card.testState.data}
          />
        ))}
      {cards.every((card) => card?.testState?.loading === false) &&
        errorCount > 0 && (
          <Alert
            severity="warning"
            sx={{
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.warning.main, 0.33)}`,
              fontSize: 13,
            }}
          >
            <AlertTitle fontSize={14}>
              {errorCount}{" "}
              {errorCount == 1 ? "Teste falhou" : "Testes falharam"}
            </AlertTitle>
            Você pode voltar e ajustar as configurações ou seguir e
            cadastrar/atualizar o host.
          </Alert>
        )}
    </Stack>
  );
}
